import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Selector from '../Inputs/Select';
import { getCountryFlag } from '../../common/countries';
import { useGetGlobetopperCountriesQuery } from '../../redux/api/public/globetopper';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { openSelect } from '../../redux/slices/select';
import { hapticsImpactLight } from '../../common/haptic';

const CountrySelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MobileCountryCircle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.background.light};
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
  margin-right: -6px;
  &:hover {
    opacity: 0.9;
  }
  @media ${PHONE_MEDIA_QUERY} {
    width: 38px;
    height: 38px;
    font-size: 20px;
  }
`;

const CountrySelector = ({
  value,
  onChange,
  label = 'Country',
  minWidth = '200px',
  selectedLabelOverride,
  selectedValueStyle,
  hideChevron = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const { data: countriesData = [] } = useGetGlobetopperCountriesQuery();

  const options = React.useMemo(
    () =>
      countriesData.map((country) => {
        const code = String(country.iso2).toUpperCase();
        const name = String(country.name);
        return {
          value: code,
          label: `${getCountryFlag(code)} ${name}`.trim(),
          flag: getCountryFlag(code),
          countryName: name,
        };
      }),
    [countriesData],
  );

  const handleMobileCircleClick = () => {
    hapticsImpactLight();
    if (options.length === 0) return;
    dispatch(
      openSelect({
        options,
        selectedOption: value,
        placeHolder: label,
        onChange: (selectedValue) => onChange(selectedValue),
      }),
    );
  };

  if (isMobile) {
    const flag = value ? getCountryFlag(value) : '';
    return (
      <MobileCountryCircle
        type="button"
        onClick={handleMobileCircleClick}
        aria-label={label}
      >
        {flag || '🌐'}
      </MobileCountryCircle>
    );
  }

  return (
    <CountrySelectorContainer>
      <Selector
        label={label}
        options={options}
        value={value}
        onChange={onChange}
        placeholder={t('spendCountrySelector.placeholder')}
        style={{ minWidth }}
        selectedLabelOverride={selectedLabelOverride}
        selectedValueStyle={selectedValueStyle}
        hideChevron={hideChevron}
      />
    </CountrySelectorContainer>
  );
};

export default CountrySelector;
